import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { usePortalMutation, usePortalQuery } from 'src/Libs/api'
import { useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { fetchConfigSyncStatus, triggerConfigSync } from '../../api/config-sync.api'
import { ConfigSyncDeviceStatusModel } from '../../models/config-sync.model'

export const configSyncStatusQueryKey = (installationId: number | undefined) => ['config-sync', 'status', installationId] as const

/**
 * Last known config-sync state per device (DB snapshot). Reflects the result of the most recent
 * sync attempt, whether triggered in this session or a previous one.
 */
export const useConfigSyncStatus = (
  installationId: number | undefined,
  enabled: boolean
): UseQueryResult<ConfigSyncDeviceStatusModel[] | null, Error> => {
  const authFetch = useAuthFetchWithResponse()

  return usePortalQuery<ConfigSyncDeviceStatusModel[]>({
    queryKey: configSyncStatusQueryKey(installationId),
    queryFn: () => fetchConfigSyncStatus(authFetch, installationId),
    enabled: enabled && !!installationId,
  })
}

interface UseConfigSyncResult {
  devices: ConfigSyncDeviceStatusModel[]
  isLoadingStatus: boolean
  isStatusError: boolean
  isSyncing: boolean
  hasSyncError: boolean
  startSync: () => Promise<void>
}

/**
 * Unified config-sync use-case hook:
 * 1) GET the last known per-device sync status on mount (DB snapshot) — only while `enabled`
 *    (i.e. an EM 360° is actually connected to the installation, otherwise there is nothing to sync yet).
 * 2) POST to trigger a new sync — this call is synchronous on the backend (it waits for every
 *    device roundtrip), so a single status refetch right after it resolves is enough to show the result.
 * Each device's own `lastUpdate` timestamp (from the GET response) is the source of truth for "last
 * synced at" — no need to track this client-side.
 */
export const useConfigSync = (installationId: number | undefined, enabled: boolean): UseConfigSyncResult => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()
  const [hasSyncError, setHasSyncError] = useState(false)

  const { data, isLoading: isLoadingStatus, isError: isStatusError } = useConfigSyncStatus(installationId, enabled)

  const triggerSyncMutation = usePortalMutation({
    mutationFn: () => triggerConfigSync(authFetch, installationId),
  })

  const startSync = useCallback(async (): Promise<void> => {
    if (!installationId) return

    setHasSyncError(false)

    try {
      await triggerSyncMutation.mutateAsync()
      await queryClient.invalidateQueries({ queryKey: configSyncStatusQueryKey(installationId) })
    } catch {
      setHasSyncError(true)
    }
  }, [installationId, queryClient, triggerSyncMutation])

  return {
    devices: data ?? [],
    isLoadingStatus,
    isStatusError,
    isSyncing: triggerSyncMutation.isPending,
    hasSyncError,
    startSync,
  }
}
